import { Injectable } from '@angular/core';
import {
  Auth,
  signOut,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signInWithPopup,
  getAuth,
  linkWithCredential,
  unlink,
  fetchSignInMethodsForEmail,
  User,
  onAuthStateChanged
} from '@angular/fire/auth';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  user$: Observable<User | null> = this.userSubject.asObservable();

  appVerifier: RecaptchaVerifier;
  confirmationResult: any;
  private isRecaptchaInitialized = false;

  constructor(private auth: Auth) { }

  // Initialize the auth listener
  initAuthListener() {
    onAuthStateChanged(this.auth, (user) => {
      this.userSubject.next(user);
    });
  }

  // Initialize RecaptchaVerifier
  async recaptcha() {
    try {
      // Clear existing verifier if it exists
      if (this.appVerifier) {
        try {
          this.appVerifier.clear();
        } catch (e) {
          console.warn('Error clearing old reCAPTCHA:', e);
        }
        this.appVerifier = null;
        // Small delay to allow DOM/library to stabilize
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Check if the container exists
      let container = document.getElementById('sign-in-button');
      if (!container) {
        console.error('reCAPTCHA container (sign-in-button) not found in DOM');
        return;
      }

      // NUCLEAR OPTION: Replace the element itself to break all internal library references
      const parent = container.parentElement;
      if (parent) {
        const newContainer = document.createElement('div');
        newContainer.id = 'sign-in-button';
        parent.replaceChild(newContainer, container);
        container = newContainer;
      } else {
        container.innerHTML = '';
      }

      console.log('🛡️ Re-initializing reCAPTCHA on fresh element');
      this.appVerifier = new RecaptchaVerifier(this.auth, container, {
        size: 'invisible',
        callback: (response) => {
          console.log('reCAPTCHA solved:', response);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });

      // Only render on web platform
      if (typeof window !== 'undefined' && window.document && !window['Capacitor']) {
        try {
          await this.appVerifier.render();
          console.log('✅ reCAPTCHA initialized successfully');
        } catch (err) {
          if (err.message && err.message.includes('already been rendered')) {
            console.log('reCAPTCHA was already rendered, continuing...');
            return;
          }
          console.warn('reCAPTCHA render error:', err);
        }
      }
    } catch (error) {
      console.error('Error initializing RecaptchaVerifier:', error);
    }
  }

  clearRecaptcha() {
    if (this.appVerifier) {
      try {
        this.appVerifier.clear();
      } catch (error) {
        console.error('Error clearing reCAPTCHA:', error);
      }
      this.appVerifier = null;
      this.isRecaptchaInitialized = false;
    }
  }

  async signInWithPhoneNumber(phoneNumber: string) {
    try {
      console.log('Attempting phone auth for:', phoneNumber);

      // Ensure reCAPTCHA is initialized
      if (!this.appVerifier || !this.isRecaptchaInitialized) {
        await this.recaptcha();
      }

      const confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this.appVerifier);
      this.confirmationResult = confirmationResult;
      return confirmationResult;
    } catch (e) {
      console.error('Phone auth error:', e);
      throw (e);
    }
  }

  get currentUser() {
    return this.auth.currentUser;
  }

  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    return signInWithPopup(auth, provider);
  }

  async linkGoogleAccount(user: User) {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(this.auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential) {
        await linkWithCredential(user, credential);
      }
    } catch (error) {
      if (error.code === 'auth/credential-already-in-use') {
        console.error('Error during Google sign-in: ', error);

        if (error.customData && error.customData.email) {
          const existingSignInMethods = await fetchSignInMethodsForEmail(this.auth, error.customData.email);
          if (existingSignInMethods.includes(GoogleAuthProvider.PROVIDER_ID)) {
            await unlink(this.auth.currentUser, GoogleAuthProvider.PROVIDER_ID);
            const result = await signInWithPopup(this.auth, provider);
            const credential = GoogleAuthProvider.credentialFromResult(result);
            if (credential) {
              await linkWithCredential(user, credential);
            }
          }
        } else {
          console.error('Missing email in error details: ', error);
        }
      } else {
        throw error;
      }
    }
  }

  async verifyOtp(otp: string) {
    try {
      if (!this.appVerifier) this.recaptcha();
      const result = await this.confirmationResult.confirm(otp);
      console.log(result);
      const user = result?.user;
      console.log(user);
    } catch (e) {
      throw (e?.message);
    }
  }

  logout() {
    return signOut(this.auth);
  }
}
